import type Delta from "quill-delta";
import { BASE_MEASURE_FONT_SIZE, MAX_SYMBOLS_IN_LINE } from "../constantsV2";
import type {
    BlockAttrs,
    InlineAttrs,
    ScaledToken,
    ScaledTokenLine,
    TextLine,
    TextSegment,
    TextToken,
    TextV2Data,
} from "../typesV2";
import {
    calculateLineWidths,
    getBulletListAdvance,
    getOrderedListAdvance,
    getSegmentFont,
    measureTextWidth,
    setCanvasFont,
} from "./measurements";

/**
 * Токенизирует строки при BASE_MEASURE_FONT_SIZE и сохраняет базовые ширины.
 * Вызывается один раз перед бинарным поиском fontSize в findBestStickerLayout.
 * Все ширины линейно масштабируются к целевому fontSize, поэтому measureTextWidth
 * не нужен внутри цикла поиска.
 */
// Базовый размер для измерений: достаточно большой чтобы canvas метрики были точными.
// При fontSize=1 дробные метрики глифов теряют точность; 100px даёт стабильные значения.

export const tokenizeLinesAtBaseScale = (
    lines: TextLine[],
    data: TextV2Data,
): ScaledTokenLine[] => {
    const baseData: TextV2Data = { ...data, fontSize: BASE_MEASURE_FONT_SIZE };
    const result: ScaledTokenLine[] = [];

    for (const line of lines) {
        const tokens: ScaledToken[] = [];

        for (const segment of line.segments) {
            const font = getSegmentFont(segment, baseData);
            setCanvasFont(font);
            const parts = segment.text.split(/(\s+)/);
            for (const part of parts) {
                if (part.length === 0) {
                    continue;
                }
                const baseWidth = measureTextWidth(part, font, true);
                tokens.push({
                    text: part,
                    width: baseWidth,
                    baseWidth,
                    color: segment.color,
                    bold: segment.bold,
                    italic: segment.italic,
                    strike: segment.strike,
                    underline: segment.underline,
                    link: segment.link,
                    isSpace: isSpaceToken(part),
                });
            }
        }

        let listMarkerBaseWidth = 0;
        if (line.listType === "bullet") {
            listMarkerBaseWidth = getBulletListAdvance(baseData);
        } else if (line.listType === "ordered") {
            listMarkerBaseWidth = getOrderedListAdvance(baseData);
        }

        result.push({
            tokens,
            listType: line.listType ?? null,
            listNumber: line.listNumber,
            listMarkerBaseWidth,
        });
    }

    return result;
};

const isWhitespaceChar = (value: string): boolean => {
    return /\s/.test(value);
};

export const cloneLines = (lines: TextLine[]): TextLine[] => {
    return lines.map((line) => ({
        segments: line.segments.map((segment) => ({
            text: segment.text,
            color: segment.color,
            bold: segment.bold,
            italic: segment.italic,
            strike: segment.strike,
            underline: segment.underline,
            link: segment.link,
        })),
        listType: line.listType,
        listNumber: line.listNumber,
    }));
};

const getWrappedListType = (
    listType: TextLine["listType"],
    isFirst: boolean,
): TextLine["listType"] => {
    if (isFirst) {
        return listType ?? null;
    }
    return listType === "ordered" ? "ordered" : null;
};

const getWrappedListNumber = (
    listType: TextLine["listType"],
    listNumber: TextLine["listNumber"],
    isFirst: boolean,
): TextLine["listNumber"] => {
    if (isFirst) {
        return listNumber;
    }
    return listType === "ordered" ? 0 : undefined;
};

/**
 * Находит позицию последнего пробела в сегментах до указанного количества символов.
 * Возвращает segmentIndex, charIndex - где резать.
 */
const findLastSpaceBefore = (
    segments: TextSegment[],
    maxChars: number,
): { segmentIndex: number; charIndex: number } | null => {
    let charCount = 0;
    let lastSpaceSegmentIndex = -1;
    let lastSpaceCharIndex = -1;

    for (let segIdx = 0; segIdx < segments.length; segIdx++) {
        const seg = segments[segIdx];
        const segLength = seg.text.length;

        if (charCount + segLength >= maxChars) {
            // Ищем пробел в текущем сегменте до maxChars
            const remaining = Math.min(Math.max(0, maxChars - charCount), segLength);
            for (let i = remaining - 1; i >= 0; i--) {
                if (isWhitespaceChar(seg.text[i])) {
                    return { segmentIndex: segIdx, charIndex: i + 1 };
                }
            }
            // Если пробел не найден в текущем сегменте, используем последний найденный
            if (lastSpaceSegmentIndex >= 0) {
                return {
                    segmentIndex: lastSpaceSegmentIndex,
                    charIndex: lastSpaceCharIndex + 1,
                };
            }
            // Если пробелов вообще нет, режем ровно по лимиту (минимум 1 символ)
            const safeCharIndex = Math.min(Math.max(1, remaining), segLength);
            return { segmentIndex: segIdx, charIndex: safeCharIndex };
        }

        // Ищем пробел в текущем сегменте
        for (let i = segLength - 1; i >= 0; i--) {
            if (isWhitespaceChar(seg.text[i])) {
                lastSpaceSegmentIndex = segIdx;
                lastSpaceCharIndex = i;
                break;
            }
        }

        charCount += segLength;
    }

    return null; // Вся строка помещается
};

/**
 * Считает общее количество символов в сегментах.
 */
const countChars = (segments: TextSegment[]): number => {
    return segments.reduce((sum, seg) => sum + seg.text.length, 0);
};

/**
 * Разрезает сегменты на две части по указанной позиции.
 */
const splitSegmentsAt = (
    segments: TextSegment[],
    segmentIndex: number,
    charIndex: number,
): { head: TextSegment[]; tail: TextSegment[] } => {
    const head: TextSegment[] = [];
    const tail: TextSegment[] = [];

    for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];

        if (i < segmentIndex) {
            // Полностью в head
            head.push(seg);
        } else if (i > segmentIndex) {
            // Полностью в tail
            tail.push(seg);
        } else {
            // Разрезаем текущий сегмент
            const headText = seg.text.substring(0, charIndex);
            const tailText = seg.text.substring(charIndex);

            if (headText.length > 0) {
                head.push({
                    text: headText,
                    color: seg.color,
                    bold: seg.bold,
                    italic: seg.italic,
                    strike: seg.strike,
                    underline: seg.underline,
                    link: seg.link,
                });
            }

            if (tailText.length > 0) {
                tail.push({
                    text: tailText,
                    color: seg.color,
                    bold: seg.bold,
                    italic: seg.italic,
                    strike: seg.strike,
                    underline: seg.underline,
                    link: seg.link,
                });
            }
        }
    }

    return { head, tail };
};

/**
 * Переносит длинные строки текста на несколько строк по ограничению длины (85 символов).
 * Алгоритм:
 * - Если строка длиннее MAX_SYMBOLS_IN_LINE (85 символов), разбивает её на части
 * - Старается переносить по пробелу (чтобы не разрывать слова)
 * - Если пробелов нет, режет ровно по лимиту символов
 * - Сохраняет все стили сегментов при разрезе
 * - Для списков (bullet/number) маркер остаётся только на первой строке элемента
 */
export const wrapLinesByMaxSymbols = (lines: TextLine[]): TextLine[] => {
    const result: TextLine[] = [];

    for (const line of lines) {
        if (line.segments.length === 0) {
            result.push({ ...line, segments: [] });
            continue;
        }

        let remainingSegments = line.segments;
        let isFirst = true;

        while (countChars(remainingSegments) > MAX_SYMBOLS_IN_LINE) {
            const splitPos = findLastSpaceBefore(remainingSegments, MAX_SYMBOLS_IN_LINE);

            if (!splitPos) {
                break;
            }

            const { head, tail } = splitSegmentsAt(
                remainingSegments,
                splitPos.segmentIndex,
                splitPos.charIndex,
            );

            result.push({
                segments: head,
                listType: getWrappedListType(line.listType, isFirst),
                listNumber: getWrappedListNumber(line.listType, line.listNumber, isFirst),
            });

            remainingSegments = tail;
            isFirst = false;
        }

        result.push({
            segments: remainingSegments,
            listType: getWrappedListType(line.listType, isFirst),
            listNumber: getWrappedListNumber(line.listType, line.listNumber, isFirst),
        });
    }

    return result;
};

/**
 * Находит позицию последнего пробела в сегментах до указанной ширины в пикселях.
 * Возвращает segmentIndex, charIndex - где резать.
 */
const isSpaceToken = (value: string): boolean => {
    return /^\s+$/.test(value);
};

const isSameStyle = (a: TextSegment, b: TextToken): boolean => {
    return (
        a.color === b.color &&
        a.bold === b.bold &&
        a.italic === b.italic &&
        a.strike === b.strike &&
        a.underline === b.underline &&
        a.link === b.link
    );
};

const appendTokenToSegments = (segments: TextSegment[], token: TextToken): void => {
    if (token.text.length === 0) {
        return;
    }
    const last = segments[segments.length - 1];
    if (last && isSameStyle(last, token)) {
        last.text += token.text;
        return;
    }
    segments.push({
        text: token.text,
        color: token.color,
        bold: token.bold,
        italic: token.italic,
        strike: token.strike,
        underline: token.underline,
        link: token.link,
    });
};

const buildLineSegmentsFromTokens = (
    tokens: TextToken[],
    start: number,
    end: number,
): TextSegment[] => {
    const segments: TextSegment[] = [];
    for (let i = start; i < end; i++) {
        appendTokenToSegments(segments, tokens[i]);
    }
    return segments;
};

const tokenizeLine = (line: TextLine, data: TextV2Data): TextToken[] => {
    const tokens: TextToken[] = [];
    for (const segment of line.segments) {
        const font = getSegmentFont(segment, data);
        setCanvasFont(font);
        const parts = segment.text.split(/(\s+)/);
        for (const part of parts) {
            if (part.length === 0) {
                continue;
            }
            const width = measureTextWidth(part, font, true);
            tokens.push({
                text: part,
                width,
                color: segment.color,
                bold: segment.bold,
                italic: segment.italic,
                strike: segment.strike,
                underline: segment.underline,
                link: segment.link,
                isSpace: isSpaceToken(part),
            });
        }
    }
    return tokens;
};

const getTokenFont = (token: TextToken, data: TextV2Data): string => {
    const fakeSegment: TextSegment = {
        text: token.text,
        color: token.color,
        bold: token.bold,
        italic: token.italic,
        strike: token.strike,
    };
    return getSegmentFont(fakeSegment, data);
};

/**
 * Вычисляет массив накопленных ширин префиксов токена за один проход.
 * prefixWidths[i] = ширина token.text.slice(0, i+1).
 */
const buildTokenPrefixWidths = (text: string, font: string): number[] => {
    setCanvasFont(font);
    const prefixWidths: number[] = new Array(text.length);
    for (let i = 0; i < text.length; i++) {
        const prefix = text.slice(0, i + 1);
        prefixWidths[i] = measureTextWidth(prefix, font, true);
    }
    return prefixWidths;
};

const splitTokenByWidth = (
    token: TextToken,
    availableWidth: number,
    data: TextV2Data,
): { headText: string; tailText: string; headWidth: number; tailWidth: number } => {
    if (availableWidth <= 0) {
        return { headText: "", tailText: token.text, headWidth: 0, tailWidth: token.width };
    }

    const font = getTokenFont(token, data);
    const prefixWidths = buildTokenPrefixWidths(token.text, font);

    // Бинарный поиск по готовому массиву — без создания строк и без measureText
    let low = 0;
    let high = token.text.length - 1;
    let splitAt = 0;

    while (low <= high) {
        const mid = (low + high) >> 1;
        if (prefixWidths[mid] <= availableWidth) {
            splitAt = mid + 1;
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }

    // Минимум 1 символ на head чтобы не зацикливаться
    if (splitAt === 0 && token.text.length > 0) {
        splitAt = 1;
    }

    const headText = token.text.slice(0, splitAt);
    const tailText = token.text.slice(splitAt);
    const headWidth = splitAt > 0 ? (prefixWidths[splitAt - 1] ?? 0) : 0;
    const tailWidth = tailText.length > 0 ? measureTextWidth(tailText, font, true) : 0;

    return { headText, tailText, headWidth, tailWidth };
};

/**
 * Переносит длинные строки текста на несколько строк по ограничению ширины в пикселях.
 * Алгоритм:
 * - Если строка шире maxWidth, разбивает её на части
 * - Старается переносить по пробелу (чтобы не разрывать слова)
 * - Если пробелов нет, режет по ширине
 * - Сохраняет все стили сегментов при разрезе
 * - Для списков (bullet/number) маркер остаётся только на первой строке элемента
 */
export const wrapLinesToWidth = (
    lines: TextLine[],
    maxWidth: number,
    data: TextV2Data,
    horizontalPadding: number,
    allowWordSplit: boolean = true,
): TextLine[] => {
    const result: TextLine[] = [];

    for (const line of lines) {
        if (line.segments.length === 0) {
            result.push({ ...line, segments: [] });
            continue;
        }

        // Вычисляем доступную ширину для текста (без отступов и маркеров)
        let availableWidth = maxWidth - 2 * horizontalPadding;
        if (line.listType === "bullet") {
            availableWidth -= getBulletListAdvance(data);
        } else if (line.listType === "ordered") {
            availableWidth -= getOrderedListAdvance(data);
        }
        if (availableWidth < 1) {
            availableWidth = 1;
        }

        const tokens = tokenizeLine(line, data);
        let index = 0;
        let isFirst = true;

        while (index < tokens.length) {
            const lineStart = index;
            let lineWidth = 0;
            let lastSpaceIndex = -1;

            while (index < tokens.length) {
                const token = tokens[index];
                if (index === lineStart && token.width > availableWidth) {
                    break;
                }
                const fits = lineWidth + token.width <= availableWidth;
                if (!fits) {
                    // Поведение как в DOM: если не влез только пробел,
                    // перенос допустим после текущего слова (пробел не рендерится в конце строки).
                    if (token.isSpace) {
                        lastSpaceIndex = index;
                    }
                    break;
                }
                lineWidth += token.width;
                if (token.isSpace) {
                    lastSpaceIndex = index;
                }
                index += 1;
            }

            if (index >= tokens.length) {
                const segments = buildLineSegmentsFromTokens(tokens, lineStart, index);
                result.push({
                    segments,
                    listType: getWrappedListType(line.listType, isFirst),
                    listNumber: getWrappedListNumber(line.listType, line.listNumber, isFirst),
                });
                break;
            }

            if (lastSpaceIndex >= lineStart) {
                // Не переносим хвостовые пробелы в конец строки:
                // в canvas они влияют на width, а в DOM (Quill) визуально схлопываются,
                // из-за чего при входе в edit текст "съезжает".
                let lineEnd = lastSpaceIndex;
                while (lineEnd > lineStart && tokens[lineEnd - 1].isSpace) {
                    lineEnd -= 1;
                }
                if (lineEnd === lineStart) {
                    lineEnd = lastSpaceIndex + 1;
                }

                const segments = buildLineSegmentsFromTokens(tokens, lineStart, lineEnd);
                result.push({
                    segments,
                    listType: getWrappedListType(line.listType, isFirst),
                    listNumber: getWrappedListNumber(line.listType, line.listNumber, isFirst),
                });
                index = lastSpaceIndex + 1;
                while (index < tokens.length && tokens[index].isSpace) {
                    index += 1;
                }
                isFirst = false;
                continue;
            }

            const token = tokens[index];

            // Для sticker-режима повторяем принцип V1:
            // не режем слово по буквам, даже если оно чуть шире доступной строки.
            if (!allowWordSplit && !token.isSpace) {
                if (index === lineStart) {
                    const segments = buildLineSegmentsFromTokens(tokens, lineStart, index + 1);
                    result.push({
                        segments,
                        listType: getWrappedListType(line.listType, isFirst),
                        listNumber: getWrappedListNumber(line.listType, line.listNumber, isFirst),
                    });
                    index += 1;
                } else {
                    const segments = buildLineSegmentsFromTokens(tokens, lineStart, index);
                    result.push({
                        segments,
                        listType: getWrappedListType(line.listType, isFirst),
                        listNumber: getWrappedListNumber(line.listType, line.listNumber, isFirst),
                    });
                    if (index <= lineStart) {
                        index = lineStart + 1;
                    }
                }
                isFirst = false;
                continue;
            }

            const available =
                lineWidth === 0 ? availableWidth : Math.max(0, availableWidth - lineWidth);
            if (available <= 0) {
                const segments = buildLineSegmentsFromTokens(tokens, lineStart, index);
                result.push({
                    segments,
                    listType: getWrappedListType(line.listType, isFirst),
                    listNumber: getWrappedListNumber(line.listType, line.listNumber, isFirst),
                });
                isFirst = false;
                continue;
            }

            const split = splitTokenByWidth(token, available, data);
            if (split.headText.length === 0) {
                const segments = buildLineSegmentsFromTokens(tokens, lineStart, index);
                result.push({
                    segments,
                    listType: getWrappedListType(line.listType, isFirst),
                    listNumber: getWrappedListNumber(line.listType, line.listNumber, isFirst),
                });
                isFirst = false;
                continue;
            }

            const segments = buildLineSegmentsFromTokens(tokens, lineStart, index);
            appendTokenToSegments(segments, {
                ...token,
                text: split.headText,
                width: split.headWidth,
                isSpace: false,
            });
            result.push({
                segments,
                listType: getWrappedListType(line.listType, isFirst),
                listNumber: getWrappedListNumber(line.listType, line.listNumber, isFirst),
            });

            tokens[index] = {
                ...token,
                text: split.tailText,
                width: split.tailWidth,
                isSpace: false,
            };
            if (tokens[index].text.length === 0) {
                index += 1;
            }
            isFirst = false;
        }
    }

    return result;
};

/**
 * Перенос строк по предвычисленным ScaledToken-ам.
 * Ширины токенов масштабируются на fontSize (baseWidth / BASE_MEASURE_FONT_SIZE * fontSize),
 * что позволяет избежать вызовов measureTextWidth внутри бинарного поиска fontSize.
 * Используется исключительно внутри findBestStickerLayout.
 */
export const wrapScaledTokensToWidth = (
    scaledLines: ScaledTokenLine[],
    fontSize: number,
    maxWidth: number,
    horizontalPaddingBase: number,
    allowWordSplit: boolean,
): TextLine[] => {
    const result: TextLine[] = [];
    const horizontalPadding = horizontalPaddingBase * fontSize;

    for (const scaledLine of scaledLines) {
        if (scaledLine.tokens.length === 0) {
            result.push({
                segments: [],
                listType: scaledLine.listType,
                listNumber: scaledLine.listNumber,
            });
            continue;
        }

        let availableWidth = maxWidth - 2 * horizontalPadding;
        if (scaledLine.listType !== null && scaledLine.listMarkerBaseWidth > 0) {
            availableWidth -= (scaledLine.listMarkerBaseWidth / BASE_MEASURE_FONT_SIZE) * fontSize;
        }
        if (availableWidth < 1) {
            availableWidth = 1;
        }

        // Создаём рабочую копию токенов с масштабированными шириной.
        // baseWidth измерен при BASE_MEASURE_FONT_SIZE, масштабируем на целевой fontSize.
        const tokens: TextToken[] = scaledLine.tokens.map((t) => ({
            ...t,
            width: (t.baseWidth / BASE_MEASURE_FONT_SIZE) * fontSize,
        }));

        let index = 0;
        let isFirst = true;

        while (index < tokens.length) {
            const lineStart = index;
            let lineWidth = 0;
            let lastSpaceIndex = -1;

            while (index < tokens.length) {
                const token = tokens[index];
                if (index === lineStart && token.width > availableWidth) {
                    break;
                }
                const fits = lineWidth + token.width <= availableWidth;
                if (!fits) {
                    if (token.isSpace) {
                        lastSpaceIndex = index;
                    }
                    break;
                }
                lineWidth += token.width;
                if (token.isSpace) {
                    lastSpaceIndex = index;
                }
                index += 1;
            }

            if (index >= tokens.length) {
                result.push({
                    segments: buildLineSegmentsFromTokens(tokens, lineStart, index),
                    listType: getWrappedListType(scaledLine.listType, isFirst),
                    listNumber: getWrappedListNumber(
                        scaledLine.listType,
                        scaledLine.listNumber,
                        isFirst,
                    ),
                });
                break;
            }

            if (lastSpaceIndex >= lineStart) {
                let lineEnd = lastSpaceIndex;
                while (lineEnd > lineStart && tokens[lineEnd - 1].isSpace) {
                    lineEnd -= 1;
                }
                if (lineEnd === lineStart) {
                    lineEnd = lastSpaceIndex + 1;
                }
                result.push({
                    segments: buildLineSegmentsFromTokens(tokens, lineStart, lineEnd),
                    listType: getWrappedListType(scaledLine.listType, isFirst),
                    listNumber: getWrappedListNumber(
                        scaledLine.listType,
                        scaledLine.listNumber,
                        isFirst,
                    ),
                });
                index = lastSpaceIndex + 1;
                while (index < tokens.length && tokens[index].isSpace) {
                    index += 1;
                }
                isFirst = false;
                continue;
            }

            const token = tokens[index];

            if (!allowWordSplit && !token.isSpace) {
                if (index === lineStart) {
                    result.push({
                        segments: buildLineSegmentsFromTokens(tokens, lineStart, index + 1),
                        listType: getWrappedListType(scaledLine.listType, isFirst),
                        listNumber: getWrappedListNumber(
                            scaledLine.listType,
                            scaledLine.listNumber,
                            isFirst,
                        ),
                    });
                    index += 1;
                } else {
                    result.push({
                        segments: buildLineSegmentsFromTokens(tokens, lineStart, index),
                        listType: getWrappedListType(scaledLine.listType, isFirst),
                        listNumber: getWrappedListNumber(
                            scaledLine.listType,
                            scaledLine.listNumber,
                            isFirst,
                        ),
                    });
                    if (index <= lineStart) {
                        index = lineStart + 1;
                    }
                }
                isFirst = false;
                continue;
            }

            // Разбиваем слово по ширине через prefix-widths (без measureText)
            const available =
                lineWidth === 0 ? availableWidth : Math.max(0, availableWidth - lineWidth);

            // Для масштабированных токенов: строим prefix-widths из baseWidth символов
            // через пропорцию, чтобы не вызывать measureText
            const scaledSource = scaledLine.tokens[index];
            const scaledPrefixWidths = buildScaledPrefixWidths(scaledSource, fontSize);

            let splitAt = 0;
            let lo = 0;
            let hi = scaledPrefixWidths.length - 1;
            while (lo <= hi) {
                const mid = (lo + hi) >> 1;
                if (scaledPrefixWidths[mid] <= available) {
                    splitAt = mid + 1;
                    lo = mid + 1;
                } else {
                    hi = mid - 1;
                }
            }
            if (splitAt === 0 && token.text.length > 0) {
                splitAt = 1;
            }

            if (splitAt === 0) {
                result.push({
                    segments: buildLineSegmentsFromTokens(tokens, lineStart, index),
                    listType: getWrappedListType(scaledLine.listType, isFirst),
                    listNumber: getWrappedListNumber(
                        scaledLine.listType,
                        scaledLine.listNumber,
                        isFirst,
                    ),
                });
                isFirst = false;
                continue;
            }

            const headText = token.text.slice(0, splitAt);
            const tailText = token.text.slice(splitAt);
            const headWidth = splitAt > 0 ? (scaledPrefixWidths[splitAt - 1] ?? 0) : 0;
            const tailWidth = splitAt < scaledPrefixWidths.length ? token.width - headWidth : 0;

            const headSegments = buildLineSegmentsFromTokens(tokens, lineStart, index);
            appendTokenToSegments(headSegments, {
                ...token,
                text: headText,
                width: headWidth,
                isSpace: false,
            });
            result.push({
                segments: headSegments,
                listType: getWrappedListType(scaledLine.listType, isFirst),
                listNumber: getWrappedListNumber(
                    scaledLine.listType,
                    scaledLine.listNumber,
                    isFirst,
                ),
            });

            tokens[index] = { ...token, text: tailText, width: tailWidth, isSpace: false };
            if (tokens[index].text.length === 0) {
                index += 1;
            }
            isFirst = false;
        }
    }

    return result;
};

/**
 * Строит массив накопленных ширин prefix-ов для ScaledToken при заданном fontSize.
 * Использует baseWidth токена пропорционально (без measureText),
 * т.к. baseWidth измерен при BASE_MEASURE_FONT_SIZE и линейно масштабируется.
 */
const buildScaledPrefixWidths = (token: ScaledToken, fontSize: number): number[] => {
    const text = token.text;
    const totalWidth = (token.baseWidth / BASE_MEASURE_FONT_SIZE) * fontSize;

    if (text.length === 0) return [];
    if (text.length === 1) return [totalWidth];

    // Распределяем ширину пропорционально между символами.
    // Для большинства шрифтов это приближение достаточно точное для бинарного поиска переноса.
    const widthPerChar = totalWidth / text.length;
    const result: number[] = new Array(text.length);
    for (let i = 0; i < text.length; i++) {
        result[i] = widthPerChar * (i + 1);
    }
    return result;
};

export const reflowTextLines = (
    originalLines: TextLine[],
    hardWidth: number,
    data: TextV2Data,
    horizontalPadding: number,
    allowWordSplit: boolean = true,
): { lines: TextLine[]; lineWidths: number[] } => {
    const lines = wrapLinesToWidth(
        originalLines,
        hardWidth,
        data,
        horizontalPadding,
        allowWordSplit,
    );
    const lineWidths = calculateLineWidths(lines, data, horizontalPadding);
    return { lines, lineWidths };
};

function hasSameStyle(segment: TextSegment, attrs: InlineAttrs, defaultColor: string) {
    return (
        segment.color === (attrs.color ?? defaultColor) &&
        segment.bold === !!attrs.bold &&
        segment.italic === !!attrs.italic &&
        segment.strike === !!attrs.strike &&
        segment.underline === !!attrs.underline &&
        segment.link === (typeof attrs.link === "string" ? attrs.link : undefined)
    );
}

function pushText(segments: TextSegment[], text: string, attrs: InlineAttrs, defaultColor: string) {
    // Чтобы не плодить сегменты: склеиваем одинаковые стили
    const last = segments[segments.length - 1];
    if (last && hasSameStyle(last, attrs, defaultColor)) {
        last.text += text;
        return segments;
    }

    const segment: TextSegment = {
        text,
        color: attrs.color ?? defaultColor,
        bold: !!attrs.bold,
        italic: !!attrs.italic,
        strike: !!attrs.strike,
        underline: !!attrs.underline,
        link: typeof attrs.link === "string" ? attrs.link : undefined,
    };

    segments.push(segment);
}

export function deltaToLines(delta: Delta, opts?: { defaultColor?: string }): TextLine[] {
    const defaultColor = opts?.defaultColor ?? "#2d2d2d";

    const out: TextLine[] = [];
    let curSegments: TextSegment[] = [];
    let orderedListCounter = 1;

    // block attrs применяются к строке, которая заканчивается текущим \n
    // поэтому мы читаем их именно на момент встречи \n
    const pushLine = (block?: BlockAttrs) => {
        const isOrdered = block?.list === "ordered";
        const listNumber = isOrdered ? orderedListCounter : undefined;

        out.push({
            segments: curSegments,
            listType: block?.list ?? null,
            listNumber,
            indent: block?.indent ?? 0,
            align: block?.align ?? "left",
        });

        if (isOrdered) {
            orderedListCounter += 1;
        } else {
            orderedListCounter = 1;
        }

        curSegments = [];
    };

    const ops = delta.ops;
    if (!ops?.length) {
        return [{ segments: [], listType: null, indent: 0 }];
    }

    for (const op of ops) {
        const ins = op.insert;
        const attrs = (op.attributes ?? {}) as InlineAttrs & BlockAttrs;

        if (typeof ins !== "string") {
            continue;
        }

        // ВАЖНО: один op может содержать несколько \n
        let start = 0;
        for (let i = 0; i < ins.length; i++) {
            if (ins[i] !== "\n") continue;

            const chunk = ins.slice(start, i); // текст до \n
            if (chunk.length > 0) {
                pushText(curSegments, chunk, attrs, defaultColor);
            }

            // \n закрывает строку, и именно на нем лежат block-атрибуты
            pushLine({
                align: attrs.align,
                list: attrs.list,
                indent: attrs.indent,
            });

            start = i + 1;
        }

        // хвост после последнего \n
        const tail = ins.slice(start);
        if (tail.length > 0) {
            pushText(curSegments, tail, attrs, defaultColor);
        }
    }

    // Если delta не закончилась \n — всё равно закрываем последнюю строку
    if (curSegments.length > 0 || out.length === 0) {
        pushLine(undefined);
    }

    return out;
}
